import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MenuBookOutlined, SearchOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import MarkdownContent from './MarkdownContent';

const KnowledgeAssistant = () => {
  const { t, i18n } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    makeApiCall('api/insights/capabilities')
      .then((response) => response.ok ? response.json() : { capabilities: [] })
      .then((payload) => {
        if (!active) return;
        const capability = (payload.capabilities || [])
          .find((item) => item.capability_key === 'knowledge_assistant');
        setEnabled(capability?.effective_enabled === true);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!enabled) return null;

  const ask = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall('api/insights/knowledge/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: question.trim(),
          locale: (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || t('insights.knowledge.failed'));
      setResult(payload);
    } catch (askError) {
      setError(askError.message || t('insights.knowledge.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }} component="section" aria-labelledby="knowledge-assistant-title">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <MenuBookOutlined sx={{ color: '#2e7d32' }} aria-hidden="true" />
          <Typography id="knowledge-assistant-title" variant="h6" component="h2">
            {t('insights.knowledge.title')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('insights.knowledge.description')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            fullWidth
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            label={t('insights.knowledge.questionLabel')}
            inputProps={{ 'aria-label': t('insights.knowledge.questionAria') }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchOutlined />}
            disabled={loading || !question.trim()}
            onClick={ask}
            aria-label={t('insights.knowledge.askAria')}
            sx={{ minWidth: { sm: 130 } }}
          >
            {t('insights.knowledge.ask')}
          </Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {result && !result.answer && (
          <Alert severity="info" sx={{ mt: 2 }}>{t('insights.knowledge.noAnswer')}</Alert>
        )}
        {result?.answer && (
          <Box sx={{ mt: 2 }}>
            <MarkdownContent>{result.answer}</MarkdownContent>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, mt: 1 }}>
              {(result.sources || []).map((source) => (
                <li key={source.id}>
                  {source.source_url ? (
                    <Link href={source.source_url} target="_blank" rel="noopener noreferrer">
                      {source.title}
                    </Link>
                  ) : source.title}
                </li>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KnowledgeAssistant;
